import {
  WSButton,
  WSCard,
  WSElement,
  WSSectionToolbar,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { WorkLog, WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { queriesPayPeriods } from "@query";

import { routeHelperPayrollRunsDetails } from "../../PayrollRuns/paths";
import { selectorPayrollRunsYear } from "../../PayrollRuns/selectors/selectorPayrollRunsYear";
import { useQueryWorkLogConvertedResource } from "../hooks/useQueryWorkLogConvertedResource";
import { useQueryWorkLogPayrollRun } from "../hooks/useQueryWorkLogPayrollRun";
import { isStatusDiscarded } from "../utils/workLogPermissions";

type Props = {
  workLog?: WorkLog;
  shimmer?: boolean;
  /**
   * Only the payroll-run row is payer-only — it links to a payer screen, and the
   * run list is scoped to the payer's account. Pay period and pay date show for
   * both, as in the design's approved contractor view.
   */
  currentUserRole?: "payer" | "payee";
};

export const PayrollDetails: React.FC<Props> = props => {
  const currentUserRole = props.currentUserRole ?? "payer";
  const payPeriodId = props.workLog?.payPeriodId ?? undefined;
  const goToPayrollRun = routeHelperPayrollRunsDetails.useGoTo();

  const queryPayPeriod = queriesPayPeriods.getPayPeriod.useQuery(
    { payPeriodId: payPeriodId! },
    { enabled: !!payPeriodId }
  );

  // Payer only: `listPayrollRuns` is scoped to the payer's own account, so a
  // contractor asking for it gets nothing back.
  const { payrollRun } = useQueryWorkLogPayrollRun(
    currentUserRole === "payer" ? props.workLog : undefined
  );

  // The statement is readable by both sides and carries the pay date directly,
  // which is what lets the contractor see it without the run.
  const { payStatement } = useQueryWorkLogConvertedResource(props.workLog);
  const payDate = payrollRun?.payDate ?? payStatement?.payDate;

  // A rejected or discarded work log is never joining a run, so "not yet" would
  // be misleading — the design says "Not available" for those terminal states.
  const isTerminal =
    props.workLog?.status === WorkLogStatus.REJECTED ||
    isStatusDiscarded(props.workLog?.status as string);

  if (props.shimmer) {
    return (
      <WSElement>
        <WSSectionToolbar title="Payroll details" />
        <WSCard shimmer />
      </WSElement>
    );
  }

  const payPeriod = queryPayPeriod.data;

  const payPeriodText =
    payPeriod && payPeriod.startDate && payPeriod.endDate
      ? `${toWSDateString(payPeriod.startDate, "monthDayYear")} - ${toWSDateString(payPeriod.endDate, "monthDayYear")}`
      : undefined;

  return (
    <WSElement>
      <WSSectionToolbar title="Payroll details" />
      <WSCard>
        <WSElement>
          <WSText.ParagraphXs color="gray500">Pay period</WSText.ParagraphXs>
          <WSText.ParagraphSm color="gray700" mt="XS">
            {payPeriodText || "—"}
          </WSText.ParagraphSm>
        </WSElement>

        {currentUserRole === "payer" && (
          <WSElement mt="M">
            <WSText.ParagraphXs color="gray500">Payroll</WSText.ParagraphXs>
            {payrollRun ? (
              <WSButton.Link
                mt="XS"
                size="S"
                onClick={() =>
                  goToPayrollRun({
                    year: selectorPayrollRunsYear(payrollRun.payDate),
                    payrollRunId: payrollRun.id ?? ""
                  })
                }
              >
                #{payrollRun.payrollNumber}
              </WSButton.Link>
            ) : (
              <WSText.ParagraphSm color="gray400" mt="XS">
                {isTerminal ? "Not available" : "Not included in a payroll yet"}
              </WSText.ParagraphSm>
            )}
          </WSElement>
        )}

        {(payDate || currentUserRole === "payer") && (
          <WSElement mt="M">
            <WSText.ParagraphXs color="gray500">Pay date</WSText.ParagraphXs>
            <WSText.ParagraphSm color="gray700" mt="XS">
              {payDate ? toWSDateString(payDate, "monthDayYear") : "—"}
            </WSText.ParagraphSm>
          </WSElement>
        )}
      </WSCard>
    </WSElement>
  );
};
